import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Box, Spinner } from '@indeed/ifl-components';
import { Helmet } from 'react-helmet';
import PageTransition from '../../components/PageTransition/PageTransition';
import { useGetAutoApplyDraftListQuery } from './__generated__/AutoApplyIndexPage.generated';
import { useInitialState } from '../../lib/context/InitialStateContext';
import { NavigateToExternalUrl } from './components/NavigateToExternalUrl';
import GettextFactory from '../../util/GettextFactory';
import { getDraftIdsNeedingQuestions } from './unfilledQuestions';

/**
 * Index page for Auto Apply flow.
 *
 * Fetches and validates draft IDs, then redirects to the first validated draft's first step —
 * its questions page when `unfilledScreenerQuestionCount` is > 0, otherwise its review page.
 * Validation ensures only auto-apply drafts are included.
 * Proctor gating is handled by AutoApplyRoot.
 */
const AutoApplyIndexPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { homepageUrl } = useInitialState();
    const gt = GettextFactory();

    // Fetch draft list from API to validate draft IDs
    const { data, loading, error } = useGetAutoApplyDraftListQuery({
        fetchPolicy: 'cache-first',
    });

    if (!loading) {
        // Validate draft IDs from URL against API response
        const param = searchParams.get('draftIds');
        const urlDraftIds = param ? param.split(',').filter(Boolean) : [];

        const drafts = data?.findReviewBeforeSubmitDrafts?.drafts ?? [];

        // Create a Set of valid draft IDs from API response for O(1) lookup
        const validDraftIdSet = new Set(drafts.map((draft) => draft.applicationDraftId));

        const draftIdsNeedingQuestions = getDraftIdsNeedingQuestions(data);

        // If query resulted in an error, skip validation and trust URL parameters
        // This ensures the app remains functional even if the validation API fails
        const validatedDraftIds = error
            ? urlDraftIds
            : // Filter URL draft IDs to only include those validated by the API
              // Preserve the order from the URL
              urlDraftIds.filter((id) => validDraftIdSet.has(id));

        if (validatedDraftIds.length > 0) {
            // Build search string with validated draftIds
            const params = new URLSearchParams(searchParams);
            params.set('draftIds', validatedDraftIds.join(','));
            const validatedSearch = `?${params.toString()}`;

            const firstDraftId = validatedDraftIds[0];
            const firstStep = draftIdsNeedingQuestions.has(firstDraftId) ? 'questions' : 'review';

            return (
                <Navigate
                    to={`/auto-apply/${firstDraftId}/${firstStep}${validatedSearch}`}
                    replace
                />
            );
        }

        return <NavigateToExternalUrl url={homepageUrl} />;
    }

    return (
        <PageTransition pageKey="auto-apply-index">
            <Helmet>
                <title>{gt.gettext('Review Applications - Indeed')}</title>
            </Helmet>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 8,
                    minBlockSize: '200px',
                }}
            >
                <Spinner title={gt.gettext('Loading applications...')} />
            </Box>
        </PageTransition>
    );
};

export default AutoApplyIndexPage;
